/**
 * EmployeeView.jsx — 관리자용 직원페이지 위장 접속
 *
 * 기능:
 *   - 직원 이름 검색 + 자동완성 드롭다운
 *   - "접속" 버튼 → POST /api/admin/impersonate → imp_token 발급
 *   - 새 탭으로 /dashboard?imp_token=XXX 열기
 *   - audit_logs에 "관리자(직원이름으로 접속)" 형식으로 기록됨
 *   - impersonation_logs 테이블에 이벤트 별도 기록
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import {
  containerStyle, cardStyle, titleStyle, descStyle, labelStyle,
  inputWrapStyle, inputStyle, dropdownStyle, dropdownItemStyle, dropdownItemHoverStyle,
  selectedCardStyle, selectedInfoStyle,
  btnPrimaryStyle, btnDisabledStyle, errorStyle, noteStyle,
} from './styles';
import { isFreelancerWorker } from '../../../utils/workerType';

export default function EmployeeView() {
  const [query, setQuery]             = useState('');    // 검색 입력값
  const [suggestions, setSuggestions] = useState([]);    // 자동완성 후보 목록
  const [showDrop, setShowDrop]       = useState(false); // 드롭다운 표시 여부
  const [selected, setSelected]       = useState(null);  // 선택된 직원 객체
  const [loading, setLoading]         = useState(false); // 직원 목록 초기 로드 중
  const [connecting, setConnecting]   = useState(false); // 위장 접속 요청 중
  const [error, setError]             = useState('');    // 오류 메시지
  const [hoveredId, setHoveredId]     = useState(null);  // 드롭다운 hover 항목 id

  const [allUsers, setAllUsers] = useState([]); // 전체 직원 목록 캐시

  const wrapRef = useRef(null); // 드롭다운 외부 클릭 감지용

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // 직원 목록 초기 로드 (employee + external + sales — admin 제외)
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get('/api/admin/impersonate/users');
        if (res.ok && Array.isArray(res.data?.users)) setAllUsers(res.data.users);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // 검색어 변경 시 클라이언트 사이드 필터링
  const handleQueryChange = useCallback((e) => {
    const val = e.target.value;
    setQuery(val);
    setSelected(null);
    setError('');
    if (!val.trim()) { setSuggestions([]); setShowDrop(false); return; }
    const lower = val.trim().toLowerCase();
    const matched = allUsers.filter(
      (u) => u.name.toLowerCase().includes(lower) || u.loginId.toLowerCase().includes(lower)
    );
    setSuggestions(matched);
    setShowDrop(true);
  }, [allUsers]);

  // 자동완성 항목 선택
  function handleSelect(user) {
    setSelected(user);
    setQuery(user.name);
    setSuggestions([]);
    setShowDrop(false);
    setError('');
  }

  // workerType 레이블: 외부직원은 소속 회사명으로 표시
  function workerTypeLabel(user) {
    if (isFreelancerWorker(user.workerType)) return '마케팅';
    if (user.workerType === 'external') return user.externalCompanyName || '외부직원';
    return '직원';
  }

  // 위장 접속 실행 — 새 탭으로 직원 대시보드 열기
  async function handleConnect() {
    if (!selected) return;
    setConnecting(true);
    setError('');
    try {
      const res = await apiClient.post('/api/admin/impersonate', { userId: selected.id });
      if (!res.ok) { setError(res.data?.message || '위장 접속에 실패했습니다.'); return; }
      const impToken = res.data?.impToken;
      if (!impToken) { setError('토큰을 받지 못했습니다.'); return; }
      // 대상 직원 권한 보고 첫 페이지 분기 — SITUATION_BOARD 있으면 상황판으로
      const permCodes = Array.isArray(res.data?.permCodes) ? res.data.permCodes : [];
      const landing = permCodes.includes('SITUATION_BOARD') ? '/my/board' : '/dashboard';
      const url = `${window.location.origin}${landing}?imp_token=${encodeURIComponent(impToken)}`;
      window.open(url, '_blank', 'noopener');
    } finally {
      setConnecting(false);
    }
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={titleStyle}>직원페이지 접속</div>
        <div style={descStyle}>
          직원의 이름을 검색해 해당 직원과 동일한 환경으로 접속합니다.<br />
          새 탭이 열리며, 모든 활동은 <strong>관리자(직원이름으로 접속)</strong> 형식으로 로그에 기록됩니다.
        </div>

        {/* 검색 입력 + 자동완성 드롭다운 */}
        <label style={labelStyle}>직원 검색</label>
        <div style={inputWrapStyle} ref={wrapRef}>
          <input
            style={inputStyle} type="text"
            placeholder={loading ? '직원 목록 로드 중...' : '이름 또는 아이디 입력...'}
            value={query} onChange={handleQueryChange}
            onFocus={() => { if (suggestions.length > 0) setShowDrop(true); }}
            disabled={loading} autoComplete="off"
          />
          {showDrop && suggestions.length > 0 && (
            <div style={dropdownStyle}>
              {suggestions.map((u) => (
                <div
                  key={u.id}
                  style={hoveredId === u.id ? dropdownItemHoverStyle : dropdownItemStyle}
                  onMouseEnter={() => setHoveredId(u.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onMouseDown={() => handleSelect(u)} // mouseDown으로 blur 전에 처리
                >
                  <strong>{u.name}</strong>
                  <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>
                    {u.loginId} · {workerTypeLabel(u)}
                    {u.department ? ` · ${u.department}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          {showDrop && suggestions.length === 0 && query.trim() && (
            <div style={dropdownStyle}>
              <div style={{ ...dropdownItemStyle, color: 'var(--text-muted)' }}>검색 결과 없음</div>
            </div>
          )}
        </div>

        {/* 선택된 직원 확인 카드 */}
        {selected && (
          <div style={selectedCardStyle}>
            <span style={{ fontSize: 28 }}>👤</span>
            <div style={selectedInfoStyle}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>{selected.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {selected.loginId} · {workerTypeLabel(selected)}
                {selected.department ? ` · ${selected.department}` : ''}
              </div>
            </div>
          </div>
        )}

        {/* 오류 메시지 */}
        {error && <div style={errorStyle}>{error}</div>}

        {/* 접속 버튼 */}
        <button
          style={selected && !connecting ? btnPrimaryStyle : btnDisabledStyle}
          disabled={!selected || connecting}
          onClick={handleConnect}
        >
          {connecting ? '접속 중...' : '새 탭으로 접속'}
        </button>

        {/* 주의 안내 */}
        <div style={noteStyle}>
          ⚠️ 위장 접속 토큰은 <strong>30분</strong> 후 만료됩니다.<br />
          해당 직원의 모든 권한으로 동작하며, 모든 활동이 로그에 기록됩니다.<br />
          관리자(admin) 계정은 위장 접속 대상이 될 수 없습니다.
        </div>
      </div>
    </div>
  );
}
